import { h, mount, toast } from '../dom.ts';
import type { Api } from '../api.ts';

// Page publique envoyée aux personnes qui écrivent depuis Apple Mail / Outlook.
// ⚠️ Procédures à valider en test réel sur les versions utilisées dans l'équipe (voir docs).
// Créer une signature ne la rend pas active : chaque logiciel exige de la désigner comme signature
// du compte (constaté sur Apple Mail le 23/09/2026). Les étapes « choisir » sont donc obligatoires.
const GUIDES: [string, string[]][] = [
  ['Apple Mail (Mac)', [
    'Cliquez sur « Copier la signature » ci-dessous.',
    'Dans Mail : menu Mail › Réglages › Signatures.',
    'Choisissez le compte à gauche, cliquez sur « + ».',
    'Décochez « Toujours utiliser ma police par défaut ».',
    'Collez (⌘V) dans la zone de droite.',
    'Renommez la signature (double-clic sur son nom, ex. « Bonjour/Bonsoir ») : vous avez sans doute déjà plusieurs signatures Signitic dans la liste.',
    'Compte toujours sélectionné à gauche, en bas de la fenêtre, menu « Choisir une signature : » — choisissez celle que vous venez de créer. Sans ça, elle n’apparaîtra pas automatiquement dans vos messages.',
    'Fermez la fenêtre. Vérifiez sur un nouveau message : la signature doit s’insérer d’elle-même.',
  ]],
  ['Outlook (Mac, Windows, nouvel Outlook)', [
    'Cliquez sur « Copier la signature ».',
    'Nouvel Outlook et Outlook Mac : Paramètres (roue dentée) › Comptes › Signatures. Outlook Windows classique : Fichier › Options › Courrier › Signatures.',
    'Créez une nouvelle signature, nommez-la (ex. « Bonjour/Bonsoir ») et collez (Ctrl/⌘ V).',
    'Sous la liste, « Signature par défaut » : choisissez-la pour les nouveaux messages ET pour les réponses / transferts, pour votre compte. Sans ça, Outlook continue d’utiliser l’ancienne signature Signitic.',
    'Enregistrez, puis vérifiez sur un nouveau message.',
  ]],
  ['iPhone / iPad (app Mail)', [
    'Ouvrez cette page sur l’iPhone, appuyez sur « Copier la signature ».',
    'Réglages › Apps › Mail › Signature.',
    'Si vous avez plusieurs comptes, choisissez « Par compte » et sélectionnez le compte concerné : la signature s’applique au compte affiché, pas aux autres.',
    'Effacez l’ancienne signature et collez. (iOS peut simplifier la mise en forme.) Il n’y a rien à « choisir » ensuite : sur iPhone, la signature du compte est utilisée automatiquement.',
  ]],
  ['Gmail (web et app)', [
    'Rien à faire : la signature est installée et mise à jour automatiquement.',
  ]],
];

async function copyRich(
  html: string,
  text: string,
  node: HTMLElement,
): Promise<void> {
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      }),
    ]);
  } catch {
    // Repli : sélection + copie (anciens navigateurs)
    const range = document.createRange();
    range.selectNode(node);
    const sel = getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    document.execCommand('copy');
    sel?.removeAllRanges();
  }
  toast('Signature copiée — collez-la dans votre logiciel de messagerie');
}

export async function installView(
  api: Pick<Api, 'install'>,
  token: string,
): Promise<HTMLElement> {
  const root = h('main', { class: 'install' });
  // État de chargement visible tout de suite : la page n'est jamais blanche pendant l'appel.
  mount(root, h('h1', null, 'Installer ma signature'), h('p', { class: 'muted' }, 'Chargement de votre signature…'));
  try {
    const data = await api.install(token);
    mount(
      root,
      h('h1', null, 'Installer ma signature'),
      h(
        'p',
        null,
        `Bonjour ${data.name}. Votre signature Gmail est déjà installée et mise à jour toute seule : rien à faire de ce côté. ` +
          'Cette page sert si vous écrivez aussi depuis Apple Mail ou Outlook — les deux cohabitent sans problème. ' +
          'Ensuite les bannières événements se mettent à jour seules : inutile de réinstaller.',
      ),
      data.signatures.map((s) => {
        const box = h('div', { class: 'sigbox', html: s.html });
        return h(
          'section',
          { class: 'panel' },
          h(
            'div',
            { class: 'row between' },
            h('strong', null, s.sendAs),
            h('span', { class: 'chip' }, s.brand),
          ),
          box,
          h(
            'button',
            { onclick: () => copyRich(s.html, s.text, box) },
            'Copier la signature',
          ),
        );
      }),
      h(
        'section',
        { class: 'panel' },
        h('h2', null, 'Où la coller ?'),
        GUIDES.map(([title, steps]) =>
          h(
            'details',
            null,
            h('summary', null, title),
            h('ol', null, steps.map((s) => h('li', null, s))),
          )
        ),
      ),
    );
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    // Un lien faux et un serveur injoignable ne se traitent pas pareil : on nomme le bon problème.
    const invalid = /lien invalide|jeton|introuvable|expir/i.test(msg);
    mount(
      root,
      h('h1', null, invalid ? 'Lien invalide' : 'Signature indisponible pour le moment'),
      h('p', null, msg),
      invalid ? null : h('p', null, h('button', { type: 'button', onclick: () => location.reload() }, 'Réessayer')),
    );
  }
  return root;
}
